import React, { useState } from 'react';
import clsx from 'clsx';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import {
  Table as TableWrapper,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  TableSortLabel,
  SortDirection,
} from '@material-ui/core';
import { DataPlaceholder } from 'features/app/components/DataPlaceholder';
import { TableColDef, TableCellDef, TableRowData, TableRowActions } from 'features/app/ts/Table';
import { ContentLoader } from 'shared/loaders';

import { useStyles } from './styles';
import i18n from 'locales/i18n';

type TableProps = {
  columns: TableColDef[];
  rowsActions?: TableRowActions[];
  cells: TableCellDef[];
  data: TableRowData[];
  ariaLabel?: string;
  empty?: string;
  initialLoading?: boolean;
  orderBy?: string;
  sortDirection?: SortDirection;
  onSort?: (field: string, value: SortDirection) => void;
};

export const Table = ({
  columns,
  rowsActions,
  cells,
  data,
  ariaLabel,
  empty,
  initialLoading,
  orderBy,
  sortDirection,
  onSort,
}: TableProps) => {
  const direction = i18n.dir()

  const classes = useStyles({ direction });

  const anchorEl = React.useRef<HTMLElement | null>(null);
  const [openedMenu, setOpenedMenu] = useState<number | undefined>();

  const createActionsClickHandler = (index: number) => {
    return (e: React.MouseEvent<HTMLButtonElement>) => {
      anchorEl.current = e.currentTarget;
      setOpenedMenu(index);
    };
  };

  const handleClose = () => {
    setOpenedMenu(undefined);
  };

  if (initialLoading) {
    return <ContentLoader />;
  }

  return (
    <TableContainer
      className={clsx(classes.tableContainer, {
        [classes.tableEmpty]: !data.length,
      })}
    >
      <TableWrapper className={classes.table} aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            {columns.map(column => {
              const handleSortChange = () => {
                if (sortDirection === 'asc') {
                  onSort?.(column.field, 'desc');
                } else {
                  onSort?.(column.field, 'asc');
                }
              };

              return (
                <TableCell
                  className={classes.tableCellHead}
                  align={column.align}
                  key={column.field}
                  sortDirection={orderBy === column.field ? sortDirection : false}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={sortDirection || undefined}
                      onClick={handleSortChange}
                      IconComponent={() => (
                        <span className={classes.tableSortIcon}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10.545"
                            height="14.5"
                            viewBox="0 0 10.545 14.5"
                          >
                            <defs></defs>
                            <path
                              className={sortDirection === 'desc' ? classes.inactiveSort : undefined}
                              d="M558.659,404.682h9.227a.656.656,0,0,0,.464-1.122l-4.614-4.614a.647.647,0,0,0-.927,0l-4.613,4.614a.659.659,0,0,0,.464,1.122Z"
                              transform="translate(-558 -398.75)"
                            />
                            <path
                              className={sortDirection === 'asc' ? classes.inactiveSort : undefined}
                              d="M567.886,407.318h-9.227a.655.655,0,0,0-.464,1.122l4.613,4.613a.647.647,0,0,0,.927,0l4.614-4.614a.659.659,0,0,0-.464-1.122Z"
                              transform="translate(-558 -398.75)"
                            />
                          </svg>
                        </span>
                      )}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
            })}

            {rowsActions && <TableCell className={clsx(classes.tableCellHead, classes.tableActions)} />}
          </TableRow>
        </TableHead>

        {data.length ? (
          <TableBody>
            {data.map((item, i) => {
              const rowId = Object.values(item).join('_');

              const actionsToDisplay = rowsActions?.filter(action => !action.shouldHide?.(item));

              return (
                <TableRow key={`row_${rowId}`}>
                  {cells.map(cell => {
                    const RenderComponent = cell.component;

                    return (
                      <TableCell className={classes.tableCell} align={cell.align} key={`cell_${cell.field}_${rowId}`}>
                        {RenderComponent ? (
                          <RenderComponent item={item[cell.field]} />
                        ) : (
                          <Typography className={classes.tableItemDefaultStyle}>{item[cell.field]}</Typography>
                        )}
                      </TableCell>
                    );
                  })}

                  {actionsToDisplay && actionsToDisplay.length > 0 && (
                    <TableCell className={clsx(classes.tableCell, classes.tableActions)}>
                      <IconButton onClick={createActionsClickHandler(i)}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        getContentAnchorEl={null}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        anchorEl={anchorEl.current}
                        keepMounted
                        open={openedMenu === i}
                        onClose={handleClose}
                        classes={{ paper: classes.menu }}
                      >
                        {actionsToDisplay?.map(action => {
                          const label = typeof action.label === 'function' ? action.label(item) : action.label;

                          return (
                            <MenuItem
                              onClick={() => {
                                action.event(item);
                                handleClose();
                              }}
                              key={label}
                            >
                              <Typography className={classes.menuItemText} variant="h6">
                                {label}
                              </Typography>
                            </MenuItem>
                          );
                        })}
                      </Menu>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        ) : null}
      </TableWrapper>

      {!data.length ? <DataPlaceholder text={empty} /> : null}
    </TableContainer>
  );
};
